import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Award, Briefcase, CheckCircle2, ExternalLink, Facebook, Globe, Instagram, Phone, Users, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getEhiaaInitiative } from '../lib/api';
import { EmptyState, LoadingScreen, PageHero, PhoneText, SectionTag } from '../components/common/BrandElements';

const asUrl = (value) => {
  if (!value) return '';
  return value.startsWith('http') ? value : `https://${value}`;
};

const clean = (value) => (value || '').trim();
const hasAny = (...values) => values.some((value) => clean(value));

const TextBlock = ({ text, className = '' }) => {
  const paragraphs = clean(text).split('\n').map((item) => item.trim()).filter(Boolean);
  if (paragraphs.length === 0) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className="leading-9 text-raad-graphite/75">
          {paragraph}
        </p>
      ))}
    </div>
  );
};

const EhiaaInitiativePage = () => {
  const { t, language } = useLanguage();
  const [initiative, setInitiative] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getEhiaaInitiative();
        setInitiative(res.data);
      } catch (error) {
        console.error('Error loading Ehiaa initiative:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return <LoadingScreen />;

  if (!initiative || !initiative.is_active) {
    return (
      <div className="min-h-screen bg-raad-porcelain pt-40">
        <EmptyState title={t('المبادرة غير متاحة حالياً', 'Initiative is not available now')} />
      </div>
    );
  }

  const getText = (ar, en) => {
    const first = language === 'ar' ? clean(ar) : clean(en);
    const fallback = language === 'ar' ? clean(en) : clean(ar);
    return first || fallback;
  };

  const title = getText(initiative.title_ar, initiative.title_en) || t('مبادرة إحياء', 'Ehiaa Initiative');
  const heroText = getText(initiative.hero_text_ar, initiative.hero_text_en);
  const partner = getText(initiative.partner_ar, initiative.partner_en);
  const intro = getText(initiative.intro_ar, initiative.intro_en);
  const impactTitle = getText(initiative.impact_title_ar, initiative.impact_title_en);
  const joinTitle = getText(initiative.join_title_ar, initiative.join_title_en);
  const joinText = getText(initiative.join_text_ar, initiative.join_text_en);
  const closing = getText(initiative.closing_ar, initiative.closing_en);

  const socialLinks = [
    { label: 'Facebook', url: clean(initiative.facebook_url), icon: Facebook },
    { label: 'Instagram', url: clean(initiative.instagram_url), icon: Instagram },
    { label: 'YouTube', url: clean(initiative.youtube_url), icon: Youtube },
  ].filter((item) => item.url);

  const impactCards = [
    {
      icon: Award,
      title: getText(initiative.training_title_ar, initiative.training_title_en),
      text: getText(initiative.training_text_ar, initiative.training_text_en),
    },
    {
      icon: Briefcase,
      title: getText(initiative.employment_title_ar, initiative.employment_title_en),
      text: getText(initiative.employment_text_ar, initiative.employment_text_en),
    },
  ].filter((card) => hasAny(card.title, card.text));

  const showIntro = hasAny(intro);
  const showImpact = hasAny(impactTitle) || impactCards.length > 0;
  const showRegistration = hasAny(joinTitle, joinText, initiative.form_url, initiative.website_url);
  const showContact = hasAny(initiative.landline_phone, initiative.mobile_phone, initiative.website_url);
  const showSocial = socialLinks.length > 0;
  const showClosing = hasAny(closing);
  const showAside = showContact || showSocial || showClosing;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="ehiaa-initiative-page">
      <PageHero
        kicker={t('مبادرة اجتماعية', 'Social Initiative')}
        title={title}
        description={heroText}
      >
        {partner && (
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-black text-raad-teal shadow-soft">
              <Users className="h-4 w-4" />
              {partner}
            </span>
          </div>
        )}
      </PageHero>

      <section className="pb-24">
        <div className={`container mx-auto grid gap-10 px-6 ${showAside ? 'lg:grid-cols-[1fr_380px]' : 'max-w-5xl'}`}>
          <main className="space-y-10">
            {showIntro && (
              <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft md:p-10">
                <SectionTag>{t('عن المبادرة', 'About the initiative')}</SectionTag>
                <TextBlock text={intro} className="mt-6 text-lg" />
              </motion.section>
            )}

            {showImpact && (
              <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                {impactTitle && (
                  <h2 className="focus-line mb-8 text-3xl font-black text-raad-charcoal">
                    {impactTitle}
                  </h2>
                )}
                {impactCards.length > 0 && (
                  <div className="grid gap-6 md:grid-cols-2">
                    {impactCards.map(({ icon: Icon, title: cardTitle, text }) => (
                      <div key={`${cardTitle}-${text}`} className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft">
                        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-raad-mist text-raad-teal">
                          <Icon className="h-7 w-7" />
                        </div>
                        {cardTitle && <h3 className="text-2xl font-black text-raad-charcoal">{cardTitle}</h3>}
                        {text && <p className="mt-4 leading-8 text-raad-graphite/70">{text}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </motion.section>
            )}

            {showRegistration && (
              <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="identity-panel rounded-[2rem] bg-raad-charcoal p-7 text-white shadow-soft md:p-10">
                <div className="relative z-10">
                  <SectionTag className="text-raad-teal-pale">{t('التسجيل', 'Registration')}</SectionTag>
                  {joinTitle && <h2 className="mt-5 text-3xl font-black">{joinTitle}</h2>}
                  <TextBlock text={joinText} className="mt-6 [&_p]:text-white/75" />
                  {(initiative.form_url || initiative.website_url) && (
                    <div className="mt-8 flex flex-wrap gap-4">
                      {initiative.form_url && (
                        <Button asChild size="lg" className="rounded-full bg-raad-teal px-8 text-white hover:bg-white hover:text-raad-charcoal">
                          <a href={initiative.form_url} target="_blank" rel="noopener noreferrer">
                            {t('التسجيل عبر الرابط المباشر', 'Register via direct link')}
                            <ExternalLink className="ms-2 h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      {initiative.website_url && (
                        <Button asChild size="lg" variant="outline" className="rounded-full border-white/30 px-8 text-white hover:bg-white/10">
                          <a href={asUrl(initiative.website_url)} target="_blank" rel="noopener noreferrer">
                            <Globe className="me-2 h-4 w-4" />
                            {initiative.website_url}
                          </a>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </motion.section>
            )}
          </main>

          {showAside && (
            <motion.aside initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }} className="space-y-6">
              <div className="sticky top-32 space-y-6">
                {showContact && (
                  <div className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft">
                    <h3 className="text-2xl font-black text-raad-charcoal">{t('التواصل والاستفسار', 'Inquiries and contact')}</h3>
                    <div className="mt-6 space-y-4 text-sm font-bold text-raad-graphite/70">
                      {initiative.landline_phone && (
                        <a href={`tel:${initiative.landline_phone}`} className="flex items-center gap-3 hover:text-raad-teal">
                          <Phone className="h-5 w-5 text-raad-teal" />
                          <span>{t('الهاتف الأرضي', 'Landline')}:</span>
                          <PhoneText>{initiative.landline_phone}</PhoneText>
                        </a>
                      )}
                      {initiative.mobile_phone && (
                        <a href={`tel:${initiative.mobile_phone}`} className="flex items-center gap-3 hover:text-raad-teal">
                          <Phone className="h-5 w-5 text-raad-teal" />
                          <span>{t('الموبايل', 'Mobile')}:</span>
                          <PhoneText>{initiative.mobile_phone}</PhoneText>
                        </a>
                      )}
                      {initiative.website_url && (
                        <a href={asUrl(initiative.website_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-raad-teal">
                          <Globe className="h-5 w-5 text-raad-teal" />
                          <span dir="ltr" className="phone-ltr">{initiative.website_url}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {showSocial && (
                  <div className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft">
                    <h3 className="text-xl font-black text-raad-charcoal">{t('روابط المبادرة', 'Initiative links')}</h3>
                    <div className="mt-5 space-y-3">
                      {socialLinks.map(({ label, url, icon: Icon }) => (
                        <a key={label} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-2xl bg-raad-mist px-4 py-3 font-bold text-raad-charcoal hover:bg-raad-teal hover:text-white">
                          <span className="flex items-center gap-3">
                            <Icon className="h-5 w-5" />
                            {label}
                          </span>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {showClosing && (
                  <div className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-1 h-6 w-6 flex-shrink-0 text-raad-teal" />
                      <p className="leading-8 text-raad-graphite/75">{closing}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.aside>
          )}
        </div>
      </section>
    </div>
  );
};

export default EhiaaInitiativePage;
